import { Body, Heading, Icon, IconType } from '@assembly-js/design-system';

import { useFeatureFlags } from 'src/context';

export const SIDEBAR_NOTIFICATION_CARD_HEIGHT = 48;

// The prototype draws the paper plane a pixel larger than the other glyphs.
const SEND_ICON: IconType = 'Send';

type SidebarNotificationCardProps = {
  icon: IconType;
  title: string;
  description: string;
  onClick: () => void;
  /** Adds a close button, revealed when the surrounding stack is hovered. */
  onDismiss?: () => void;
  dismissLabel?: string;
  testId?: string;
};

/** The onboarding-activation style: lighter type, smaller icon tile, and a border that darkens on hover. */
function LightSidebarNotificationCard({
  icon,
  title,
  description,
  onClick,
  onDismiss,
  dismissLabel,
  testId,
}: SidebarNotificationCardProps) {
  return (
    <div
      className="group/card relative box-border h-12 w-full rounded-lg border border-solid border-gray-150 bg-white transition-colors duration-150 hover:border-gray-300"
      data-testid={testId}
    >
      <button
        className={`box-border flex size-full cursor-pointer appearance-none items-center gap-2 border-0 bg-transparent py-0 pl-3 text-left ${
          onDismiss ? 'pr-[35px]' : 'pr-3'
        }`}
        onClick={onClick}
        type="button"
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded bg-gray-150">
          <Icon
            className={`text-secondary ${icon === SEND_ICON ? 'size-[15px]' : 'size-3.5'}`}
            icon={icon}
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <Body
            className="truncate leading-[18px] text-black-heading"
            size="sm"
            tag="span"
          >
            {title}
          </Body>
          <Body
            className="truncate leading-[16px] text-secondary"
            size="xs"
            tag="span"
          >
            {description}
          </Body>
        </span>
      </button>
      {onDismiss && (
        <button
          aria-label={dismissLabel}
          className="absolute right-[7px] top-[13px] flex size-5 cursor-pointer appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-gray-300 opacity-0 transition-opacity hover:text-secondary focus-visible:opacity-100 group-hover/card:opacity-100"
          onClick={onDismiss}
          type="button"
        >
          <Icon className="size-2.5" icon="Close" />
        </button>
      )}
    </div>
  );
}

export function SidebarNotificationCard(props: SidebarNotificationCardProps) {
  const { EnableOnboardingActivation } = useFeatureFlags();
  if (EnableOnboardingActivation) {
    return <LightSidebarNotificationCard {...props} />;
  }
  return <ClassicSidebarNotificationCard {...props} />;
}

function ClassicSidebarNotificationCard({
  icon,
  title,
  description,
  onClick,
  onDismiss,
  dismissLabel,
  testId,
}: SidebarNotificationCardProps) {
  return (
    // Preflight is off, so without box-border the border would add 2px to
    // the 48px the stack lays cards out by.
    <div
      className="relative box-border h-12 w-full rounded border border-solid border-secondary-hover bg-white"
      data-testid={testId}
    >
      <button
        // Dismissible cards keep the title clear of the corner close button.
        className={`box-border flex size-full cursor-pointer appearance-none items-center gap-2 border-0 bg-transparent py-1.5 pl-2 text-left ${
          onDismiss ? 'pr-8' : 'pr-3'
        }`}
        onClick={onClick}
        type="button"
      >
        <span className="flex shrink-0 items-center justify-center rounded bg-secondary-hover p-[9px]">
          <Icon className="size-3.5 text-light-gray" icon={icon} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <Heading
            className="mb-0! truncate leading-[20px] text-black-heading"
            size="xs"
            tag="span"
          >
            {title}
          </Heading>
          <Body
            className="truncate text-[10px]! leading-[16px] text-secondary"
            size="xs"
            tag="span"
          >
            {description}
          </Body>
        </span>
      </button>
      {onDismiss && (
        <button
          aria-label={dismissLabel}
          className="absolute right-3 top-[9px] flex cursor-pointer appearance-none items-center border-0 bg-transparent p-0 text-gray-300 opacity-0 transition-opacity hover:text-secondary group-hover/notifications:opacity-100 group-focus-within/notifications:opacity-100"
          onClick={onDismiss}
          type="button"
        >
          <Icon className="size-3.5" icon="Close" />
        </button>
      )}
    </div>
  );
}
